import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuBellRing, LuClock, LuPause, LuSave, LuSend, LuFlaskConical, LuPlay } from 'react-icons/lu';
import { Button, LoadingSpinner, ToggleSwitch, confirm } from 'components/common';
import { INPUT_CLS } from 'helpers/formStyles';
import { getReminder, saveReminder, sendReminderNow, sendReminderTest } from './remindersApi';
import {
  DAYS, MONTH_DAYS, formatInZone, isIncomplete, isMonthly, isTask, slotLabel, toForm, toPayload, zoneOffsetLabel,
} from './reminderFormat';
import ReminderFieldInput from './ReminderFieldInput';
import { RecipientPreview, RunHistory } from './ReminderActivity';
import ReminderZoneClock from './ReminderZoneClock';
import { MissedSlotsAlert } from './ReminderHealth';

// The settings of ONE scheduled reminder (backend Reminders::Base subclass): on/off,
// slot times and days (common to every reminder), the reminder's own options (drawn
// from what it declares), then who would get what now and the last runs. Every time
// on the page is in the reminder's own time zone (default Asia/Ho_Chi_Minh). Used on
// App Settings → Reminders and inside a module's own settings (Payment Request).

const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

const ReminderSettingsCard = ({ reminderKey, onSaved }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const busyRef = useRef(false);

  const apply = (payload) => {
    setData(payload);
    setForm(toForm(payload.definition, payload.settings));
  };

  const load = useCallback(async () => {
    setError('');
    try { apply((await getReminder(reminderKey)).data); }
    catch (e) { setError(errorOf(e, 'Could not load this reminder.')); }
  }, [reminderKey]);
  useEffect(() => { load(); }, [load]);

  const definition = data?.definition;
  const dirty = useMemo(() => !!(data && form) &&
    JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(toForm(data.definition, data.settings))), [data, form]);
  const incomplete = !!(definition && form) && isIncomplete(definition, form);

  // Saved recipients keep their names in the picker even though it stores emails.
  const knownUsers = useMemo(() => (definition?.fields || [])
    .filter((f) => f.type === 'users')
    .flatMap((f) => data.settings.options[f.key] || [])
    .map((u) => ({ value: u.email, email: u.email, label: u.name, name: u.name })), [definition, data]);

  const set = (patch) => setForm((old) => ({ ...old, ...patch }));
  const setTime = (slot, value) => setForm((old) => ({ ...old, times: { ...old.times, [slot]: value } }));
  const setOption = (key, value) => setForm((old) => ({ ...old, options: { ...old.options, [key]: value } }));
  const toggleDay = (day) => setForm((old) => ({
    ...old, days: old.days.includes(day) ? old.days.filter((d) => d !== day) : [...old.days, day],
  }));
  const toggleMonthDay = (day) => setForm((old) => {
    const list = old.month_days || [];
    return { ...old, month_days: list.includes(day) ? list.filter((d) => d !== day) : [...list, day] };
  });

  // One action at a time — a second click while one is running does nothing.
  const run = async (name, action) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(name);
    try { await action(); }
    catch (e) { toast.error(errorOf(e, 'Something went wrong. Try again.')); }
    finally { busyRef.current = false; setBusy(''); }
  };

  const save = () => run('save', async () => {
    const res = await saveReminder(reminderKey, toPayload(form));
    apply(res.data);
    toast.success(res.message || 'Reminder saved.');
    onSaved?.(res.data);
  });

  const sendTest = () => run('test', async () => {
    toast.success((await sendReminderTest(reminderKey)).message);
  });

  const sendNow = () => run('now', async () => {
    const first = definition.slots[0];
    const ok = await confirm(task ? {
      title: 'Run it now?',
      message: `${definition.label} runs now. It counts as today's ${first.label.toLowerCase()}, so the schedule will not run it again today.`,
      confirmText: 'Run now',
    } : {
      title: 'Send it now?',
      message: `Everyone in the preview gets the ${first.label.toLowerCase()} now. It counts as today's ${first.label.toLowerCase()}, so the schedule will not send it again; later runs still go out.`,
      confirmText: 'Send now',
    });
    if (!ok) return;
    const res = await sendReminderNow(reminderKey);
    apply(res.data);
    toast.success(res.message);
  });

  if (!form) {
    return (
      <div className="tw-py-10 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-sm tw-text-slate-500">
        {error ? <><span role="alert">{error}</span><Button variant="secondary" size="sm" onClick={load}>Retry</Button></> : <LoadingSpinner />}
      </div>
    );
  }

  const saveFirst = dirty ? 'Save your changes first' : undefined;
  // A scheduled task (sync, monthly report) runs one job — no recipients, no test email.
  const task = isTask(definition);
  const monthly = isMonthly(definition);
  const savedZone = data.settings.time_zone;
  // The zone being edited labels the time boxes; the saved one labels what already ran.
  const zoneLabel = zoneOffsetLabel(form.time_zone) || form.time_zone;
  const zoneOptions = data.zone?.options || [];
  const knownZone = zoneOptions.some((z) => z.value === form.time_zone);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <section aria-label={`${definition.label} reminder`} className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200">
        <header className="tw-flex tw-flex-wrap tw-items-start tw-gap-3 tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
          <div className="tw-min-w-0 tw-flex-1">
            <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-slate-900">{definition.label}</h2>
            <p className="tw-m-0 tw-text-[13px] tw-text-slate-500">{definition.description}</p>
            <p className="tw-m-0 tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-text-[12px] tw-text-slate-600">
              <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-font-semibold ${data.settings.enabled ? 'tw-text-emerald-700' : 'tw-text-slate-500'}`}>
                {data.settings.enabled ? <LuBellRing size={13} /> : <LuPause size={13} />}{data.settings.enabled ? 'On' : 'Off'}
              </span>
              {data.next_run && (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <LuClock size={13} />Next: {slotLabel(definition, data.next_run.slot)} · {formatInZone(data.next_run.at, savedZone, true)} ({zoneOffsetLabel(savedZone)})
                </span>
              )}
            </p>
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {!task && (
              <Button variant="secondary" size="sm" leftIcon={LuFlaskConical} loading={busy === 'test'} disabled={!!busy || dirty}
                title={saveFirst} onClick={sendTest}>Send test to me</Button>
            )}
            <Button variant="secondary" size="sm" leftIcon={task ? LuPlay : LuSend} loading={busy === 'now'} disabled={!!busy || dirty}
              title={saveFirst} onClick={sendNow}>{task ? 'Run now' : 'Send now'}</Button>
            <Button variant="primary" size="sm" leftIcon={LuSave} loading={busy === 'save'} disabled={!!busy || !dirty || incomplete}
              onClick={save}>Save</Button>
          </div>
        </header>

        <div className="tw-p-4 tw-flex tw-flex-col tw-gap-5">
          <MissedSlotsAlert missed={data.missed_today} timeZone={savedZone} />
          <ToggleSwitch value={form.enabled} onChange={(on) => set({ enabled: on })} label={task ? 'Run automatically' : 'Send automatically'} />

          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <label htmlFor={`reminder-zone-${reminderKey}`} className="tw-text-[13px] tw-font-medium tw-text-slate-700">Time zone</label>
              <select id={`reminder-zone-${reminderKey}`} value={form.time_zone} onChange={(e) => set({ time_zone: e.target.value })}
                className={`${INPUT_CLS} tw-w-auto tw-min-w-[240px]`}>
                {!knownZone && <option value={form.time_zone}>{form.time_zone}</option>}
                {zoneOptions.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
              </select>
            </div>
            <ReminderZoneClock key={form.time_zone} timeZone={form.time_zone} serverNow={data.zone?.now} />
          </div>

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
            {definition.slots.map((slot) => (
              <div key={slot.key} className="tw-min-w-0">
                <label htmlFor={`reminder-slot-${slot.key}`} className="tw-block tw-text-[13px] tw-font-medium tw-text-slate-700 tw-mb-1">
                  {slot.label} ({zoneLabel})
                </label>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <input id={`reminder-slot-${slot.key}`} type="time" className={`${INPUT_CLS} tw-w-32`}
                    value={form.times[slot.key] || ''} onChange={(e) => setTime(slot.key, e.target.value)} />
                  {slot.optional && form.times[slot.key] && (
                    <Button variant="ghost" size="sm" onClick={() => setTime(slot.key, '')}>Turn off</Button>
                  )}
                </div>
                {slot.hint && <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-slate-500">{slot.hint}</p>}
              </div>
            ))}
            {monthly ? (
              <fieldset className="tw-m-0 tw-p-0 tw-border-0 tw-min-w-0 md:tw-col-span-2">
                <legend className="tw-p-0 tw-text-[13px] tw-font-medium tw-text-slate-700 tw-mb-1">Days of the month</legend>
                <div className="tw-grid tw-grid-cols-7 tw-gap-1 tw-max-w-[340px]">
                  {MONTH_DAYS.map((d) => {
                    const on = (form.month_days || []).includes(d);
                    return (
                      <button key={d} type="button" aria-pressed={on} aria-label={`Day ${d}`} onClick={() => toggleMonthDay(d)}
                        className={`tw-h-8 tw-rounded-lg tw-border tw-border-solid tw-text-[12px] tw-font-semibold tw-tabular-nums tw-cursor-pointer tw-transition-colors ${
                          on ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white' : 'tw-bg-white tw-border-slate-200 tw-text-slate-500 hover:tw-bg-slate-50'}`}>
                        {d}
                      </button>
                    );
                  })}
                </div>
                <p className="tw-m-0 tw-mt-1 tw-text-[12px] tw-text-slate-500">1–28 only, so every month has the day.</p>
              </fieldset>
            ) : (
            <fieldset className="tw-m-0 tw-p-0 tw-border-0 tw-min-w-0">
              <legend className="tw-p-0 tw-text-[13px] tw-font-medium tw-text-slate-700 tw-mb-1">Days</legend>
              <div className="tw-flex tw-flex-wrap tw-gap-1">
                {DAYS.map((d) => {
                  const on = form.days.includes(d.value);
                  return (
                    <button key={d.value} type="button" aria-pressed={on} onClick={() => toggleDay(d.value)}
                      className={`tw-h-8 tw-min-w-[42px] tw-rounded-lg tw-border tw-border-solid tw-text-[12px] tw-font-semibold tw-cursor-pointer tw-transition-colors ${
                        on ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white' : 'tw-bg-white tw-border-slate-200 tw-text-slate-500 hover:tw-bg-slate-50'}`}>
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
            )}
          </div>

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
            {definition.fields.map((field) => (
              <div key={field.key} className={field.type === 'multi' ? 'md:tw-col-span-3' : ''}>
                <ReminderFieldInput field={field} value={form.options[field.key]} knownUsers={knownUsers}
                  onChange={(value) => setOption(field.key, value)} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {!task && <RecipientPreview columns={definition.preview_columns} preview={data.preview || []} previewTotal={data.preview_total}
        unassigned={data.unassigned || []} summaryRecipients={data.summary_recipients || []} timeZone={savedZone} />}
      <RunHistory definition={definition} runs={data.runs || []} timeZone={savedZone} />
    </div>
  );
};

ReminderSettingsCard.propTypes = {
  reminderKey: PropTypes.string.isRequired,
  onSaved: PropTypes.func,
};

export default ReminderSettingsCard;
